import * as THREE from 'three';
import { QUAD_VERT, RESOLVE_FRAG, pass1Fragment } from './shaders/passes.glsl.js';
import { SURFACE_GLSL } from './shaders/surfaces.glsl.js';

/**
 * Procedural PBR material factory — GPU edition.
 *
 * Every surface in the game is authored as a *shader program* (see
 * `shaders/surfaces.glsl.js`) that returns albedo, height, roughness, metalness
 * and cavity AO for a point in [0,1)^2. The forge runs that program over a
 * fullscreen quad into render targets and derives the normal map from the
 * resulting height field, so silhouette detail and lighting response stay
 * consistent.
 *
 * Two passes per surface:
 *
 *   1. MRT, half-float: albedo(linear) + grime mask, and ao/rough/metal/height.
 *      Half-float matters — an 8-bit height field Sobels into visibly banded
 *      normals.
 *   2. MRT, RGBA8: reads the height neighbourhood once and emits all three
 *      final textures. The neighbourhood gives the Sobel normal *and* the
 *      cavity/curvature signals that drive grime-in-crevices and wear-on-edges.
 *
 * Rationale for procedural-only: the game ships with no binary assets, so it
 * loads instantly from source and every material can be re-tuned without an art
 * pipeline. Doing it on the GPU is ~40x faster than the old CPU rasteriser,
 * which buys enough headroom to layer macro -> meso -> micro detail properly.
 */

export const TUNING = {
  /** Texture edge length per quality level. */
  size: { ultra: 1024, high: 512, medium: 256 },
  /** Cavity/curvature extraction (radii are in uv units, so resolution independent). */
  cavity: { gain: 3.4, fineRadius: 0.0035, broadRadius: 0.0125 },
  /** Global multiplier on the height -> normal Sobel gain. */
  sobel: 1.0,
  /** Default large-scale tiling break-up. */
  macroTint: 0.085,
  macroRough: 0.07,
  /** Cap on anisotropic filtering. */
  maxAnisotropy: 16,
};

export class AssetForge {
  constructor(renderer, quality = 'ultra') {
    this.renderer = renderer;
    this.quality = quality;
    this.maxAniso = renderer.capabilities.getMaxAnisotropy();
    this.cache = new Map();
    this.texSize = TUNING.size[quality] ?? TUNING.size.ultra;

    /** Half-float is needed for a clean height field; fall back if unsupported. */
    this._hdrType = renderer.extensions.has('EXT_color_buffer_float')
      ? THREE.HalfFloatType
      : THREE.UnsignedByteType;

    this._temps = new Map(); // size -> intermediate MRT
    this._targets = []; // every output RT, for dispose()
    this._rig = null;
    this._resolveMat = null;
    this.stats = { surfaces: 0, ms: 0 };
  }

  /** Returns a cached MeshStandardMaterial for a named surface. */
  material(id, overrides = {}) {
    const key = id + JSON.stringify(overrides);
    if (this.cache.has(key)) return this.cache.get(key);
    const def = SURFACES[id];
    if (!def) throw new Error(`[forge] unknown surface "${id}"`);

    const maps = this._maps(id, def);

    const mat = new THREE.MeshStandardMaterial({
      map: maps.albedo,
      normalMap: maps.normal,
      roughnessMap: maps.orm,
      metalnessMap: maps.orm,
      aoMap: maps.orm,
      roughness: 1,
      metalness: def.metal ?? 1,
      normalScale: new THREE.Vector2(def.normalScale ?? 1, def.normalScale ?? 1),
      envMapIntensity: def.envIntensity ?? 1,
      ...overrides,
    });
    mat.name = `forge:${id}`;
    mat.userData.materialId = def.materialId ?? id;
    mat.userData.uvScale = def.uvScale ?? 1;
    this.cache.set(key, mat);
    return mat;
  }

  /** Convenience: clone a surface material with a different UV repeat. */
  tiled(id, repeatX, repeatY = repeatX, overrides = {}) {
    const key = `${id}|${repeatX}x${repeatY}|${JSON.stringify(overrides)}`;
    if (this.cache.has(key)) return this.cache.get(key);
    const base = this.material(id, overrides);
    const mat = base.clone();
    // The GPU texture is shared; each material just needs its own uv transform,
    // so we hand out lightweight views onto the same render-target texture.
    for (const slot of ['map', 'normalMap', 'roughnessMap', 'metalnessMap', 'aoMap']) {
      if (!mat[slot]) continue;
      const t = this._view(mat[slot]);
      t.repeat.set(repeatX, repeatY);
      t.wrapS = t.wrapT = THREE.RepeatWrapping;
      mat[slot] = t;
    }
    mat.name = `forge:${id}@${repeatX}x${repeatY}`;
    mat.userData = { ...base.userData };
    this.cache.set(key, mat);
    return mat;
  }

  /** Pre-generate a set of surfaces (defaults to all). Returns milliseconds. */
  warm(ids = Object.keys(SURFACES)) {
    const t0 = performance.now();
    for (const id of ids) this.material(id);
    return performance.now() - t0;
  }

  dispose() {
    for (const m of this.cache.values()) m.dispose();
    this.cache.clear();
    for (const rt of this._targets) rt.dispose();
    this._targets.length = 0;
    for (const rt of this._temps.values()) rt.dispose();
    this._temps.clear();
    if (this._rig) {
      this._rig.geometry.dispose();
      this._rig = null;
    }
    this._resolveMat?.dispose();
    this._resolveMat = null;
  }

  /* ------------------------------------------------------------------ *
   * Generation                                                          *
   * ------------------------------------------------------------------ */

  /** Runs both passes for a surface and returns its three textures. */
  _maps(id, def) {
    const t0 = performance.now();
    const renderer = this.renderer;
    const size = Math.max(16, Math.round(this.texSize * (def.sizeScale ?? 1)));

    // --- save every bit of renderer state we are about to touch -------------
    const prevRT = renderer.getRenderTarget();
    const prevCube = renderer.getActiveCubeFace();
    const prevMip = renderer.getActiveMipmapLevel();
    const prevAutoClear = renderer.autoClear;
    const prevClearColor = renderer.getClearColor(new THREE.Color());
    const prevClearAlpha = renderer.getClearAlpha();

    renderer.autoClear = true;
    renderer.setClearColor(0x000000, 1);

    const rig = this._ensureRig();
    const temp = this._tempTarget(size);
    const out = this._outputTarget(size, id);

    // --- pass 1: evaluate the surface program -------------------------------
    const mat1 = new THREE.ShaderMaterial({
      glslVersion: THREE.GLSL3,
      vertexShader: QUAD_VERT,
      fragmentShader: pass1Fragment(SURFACE_GLSL[id]),
      uniforms: {
        uMacroTint: { value: def.macroTint ?? TUNING.macroTint },
        uMacroRough: { value: def.macroRough ?? TUNING.macroRough },
        uSeed: { value: def.seed ?? 0 },
      },
      depthTest: false,
      depthWrite: false,
    });
    rig.mesh.material = mat1;
    renderer.setRenderTarget(temp);
    renderer.render(rig.scene, rig.camera);

    // --- pass 2: normal from height + cavity dirt + edge wear ---------------
    const res = this._ensureResolve();
    const u = res.uniforms;
    u.tAlbedo.value = temp.textures[0];
    u.tORMH.value = temp.textures[1];
    u.uTexel.value.set(1 / size, 1 / size);
    u.uSobel.value = (def.heightScale ?? 2) * TUNING.sobel * (size / 512);
    u.uCavityR.value.set(
      Math.max(1.5 / size, TUNING.cavity.fineRadius),
      Math.max(4.0 / size, TUNING.cavity.broadRadius)
    );
    u.uCavityGain.value = def.cavityGain ?? TUNING.cavity.gain;
    u.uCavityAO.value = def.cavityAO ?? 0.5;
    u.uDirtColor.value.copy(def.dirtColor);
    u.uDirt.value = def.dirt ?? 0.5;
    u.uWearColor.value.copy(def.wearColor);
    u.uWear.value = def.wear ?? 0.3;
    u.uWearMetal.value = def.wearMetal ?? 0;

    rig.mesh.material = res;
    renderer.setRenderTarget(out);
    renderer.render(rig.scene, rig.camera);

    mat1.dispose();
    // Drop the references to the shared intermediate: the next surface renders
    // *into* it, and a sampler still pointing at it would be a feedback loop.
    u.tAlbedo.value = null;
    u.tORMH.value = null;

    // --- restore ------------------------------------------------------------
    renderer.setRenderTarget(prevRT, prevCube, prevMip);
    renderer.autoClear = prevAutoClear;
    renderer.setClearColor(prevClearColor, prevClearAlpha);

    this.stats.surfaces++;
    this.stats.ms += performance.now() - t0;

    return { albedo: out.textures[0], orm: out.textures[1], normal: out.textures[2] };
  }

  /** Fullscreen-quad rig: one triangle pair, one ortho-free clip-space vertex shader. */
  _ensureRig() {
    if (this._rig) return this._rig;
    const geometry = new THREE.PlaneGeometry(2, 2);
    const mesh = new THREE.Mesh(geometry, null);
    mesh.frustumCulled = false;
    const scene = new THREE.Scene();
    scene.add(mesh);
    // The vertex shader writes clip space directly, so the camera is a formality.
    const camera = new THREE.Camera();
    this._rig = { geometry, mesh, scene, camera };
    return this._rig;
  }

  _ensureResolve() {
    if (this._resolveMat) return this._resolveMat;
    this._resolveMat = new THREE.ShaderMaterial({
      glslVersion: THREE.GLSL3,
      vertexShader: QUAD_VERT,
      fragmentShader: RESOLVE_FRAG,
      uniforms: {
        tAlbedo: { value: null },
        tORMH: { value: null },
        uTexel: { value: new THREE.Vector2() },
        uSobel: { value: 1 },
        uCavityR: { value: new THREE.Vector2() },
        uCavityGain: { value: TUNING.cavity.gain },
        uCavityAO: { value: 0.5 },
        uDirtColor: { value: new THREE.Color() },
        uDirt: { value: 0.5 },
        uWearColor: { value: new THREE.Color() },
        uWear: { value: 0.3 },
        uWearMetal: { value: 0 },
      },
      depthTest: false,
      depthWrite: false,
    });
    return this._resolveMat;
  }

  /** Shared half-float intermediate (one per distinct size). */
  _tempTarget(size) {
    let rt = this._temps.get(size);
    if (rt) return rt;
    rt = new THREE.WebGLRenderTarget(size, size, {
      count: 2,
      type: this._hdrType,
      format: THREE.RGBAFormat,
      depthBuffer: false,
      stencilBuffer: false,
      generateMipmaps: false,
      minFilter: THREE.LinearFilter,
      magFilter: THREE.LinearFilter,
      wrapS: THREE.RepeatWrapping,
      wrapT: THREE.RepeatWrapping,
    });
    // Wrapped taps in the resolve pass are what keep the normal map tileable.
    for (const t of rt.textures) {
      t.colorSpace = THREE.NoColorSpace;
      t.wrapS = t.wrapT = THREE.RepeatWrapping;
    }
    this._temps.set(size, rt);
    return rt;
  }

  /**
   * Final RGBA8 target: [0] albedo (sRGB), [1] ORM, [2] normal.
   *
   * Render targets do not mipmap by default — `generateMipmaps` plus a mipmap
   * minFilter is what makes three allocate the chain and regenerate it after
   * every render, and without it every surface aliases into shimmering noise at
   * distance.
   */
  _outputTarget(size, id) {
    const rt = new THREE.WebGLRenderTarget(size, size, {
      count: 3,
      type: THREE.UnsignedByteType,
      format: THREE.RGBAFormat,
      depthBuffer: false,
      stencilBuffer: false,
      generateMipmaps: true,
      minFilter: THREE.LinearMipmapLinearFilter,
      magFilter: THREE.LinearFilter,
      wrapS: THREE.RepeatWrapping,
      wrapT: THREE.RepeatWrapping,
      anisotropy: Math.min(TUNING.maxAnisotropy, this.maxAniso),
    });
    const names = ['albedo', 'orm', 'normal'];
    rt.textures.forEach((t, i) => {
      t.name = `${id}:${names[i]}`;
      // Only the albedo is colour data. Marking it sRGB makes three allocate an
      // SRGB8_ALPHA8 attachment, so the hardware encodes on write and decodes on
      // sample — the ORM and normal maps stay strictly linear.
      t.colorSpace = i === 0 ? THREE.SRGBColorSpace : THREE.NoColorSpace;
      t.wrapS = t.wrapT = THREE.RepeatWrapping;
      t.minFilter = THREE.LinearMipmapLinearFilter;
      t.magFilter = THREE.LinearFilter;
      t.generateMipmaps = true;
      t.anisotropy = Math.min(TUNING.maxAnisotropy, this.maxAniso);
    });
    this._targets.push(rt);
    return rt;
  }

  /**
   * A second Texture object pointing at the same GPU texture.
   *
   * `texture.repeat` lives on the Texture, not on the GL object, so a material
   * that needs a different tiling needs its own Texture — but cloning a render
   * target texture would hand three an object with no GL handle. Copying the
   * handle across gives a view that shares storage, filtering and mipmaps.
   */
  _view(tex) {
    const props = this.renderer.properties;
    const handle = props.get(tex).__webglTexture;
    if (handle === undefined) return tex; // not initialised yet — should not happen
    const v = tex.clone();
    v.isRenderTargetTexture = true; // keeps three from trying to upload it
    props.get(v).__webglTexture = handle;
    return v;
  }
}

/* ------------------------------------------------------------------ *
 * Surface library.                                                    *
 *                                                                     *
 * The pixels come from `shaders/surfaces.glsl.js`; what lives here is  *
 * the per-surface response of the shared cavity/wear system plus the   *
 * gameplay-facing metadata (physics material id, texel budget).        *
 *                                                                     *
 *   dirt/dirtColor   grime that settles in crevices                    *
 *   wear/wearColor   material exposed on high-curvature peaks          *
 *   wearMetal        1 => wear exposes bare metal (metalness 1, glossy) *
 *   cavityAO         how hard cavities darken the occlusion channel    *
 *   heightScale      height -> normal gain                             *
 * ------------------------------------------------------------------ */

const srgb = (hex) => new THREE.Color(hex); // ColorManagement converts to linear

export const SURFACES = {
  concrete: {
    materialId: 'concrete',
    metal: 0,
    heightScale: 1.7,
    normalScale: 1.0,
    dirt: 0.60,
    dirtColor: srgb(0x2a2620),
    /**
     * Measured: wear moves concrete's mean albedo by 0.4%, so it is not what was
     * blowing out kerb tops and stair nosings — that is the base albedo meeting
     * the low sun at close to normal incidence, and it is handled in the shader.
     * The colour still comes down a little because a 0.33-linear wear tone on a
     * 0.176-linear surface is a bright rim on precisely the nosings in question.
     */
    wear: 0.16,
    wearColor: srgb(0x8e8b84),
    wearMetal: 0,
    cavityAO: 0.55,
    seed: 11,
  },

  metalPainted: {
    materialId: 'metal',
    metal: 1,
    heightScale: 2.4,
    normalScale: 1.2,
    envIntensity: 1.1,
    dirt: 0.55,
    dirtColor: srgb(0x241d16),
    // Bare steel only shows on chip edges and scratch peaks. At 0.60 the fine
    // brush striations were enough curvature to wash the whole panel in bright
    // metal, which is what made a painted wall read as polished aluminium.
    wear: 0.16,
    wearColor: srgb(0x8e8a85),
    wearMetal: 1,
    cavityAO: 0.5,
    seed: 23,
  },

  corrugated: {
    materialId: 'metal',
    metal: 1,
    heightScale: 3.0,
    /**
     * Rib crests were clipping to flat white against the low sun while the sheet
     * read as flat plywood head-on. Both are the same fault: at metalness 0.82 the
     * rib was carried by the specular lobe alone, and a specular-only feature is
     * present or absent depending on where the mirror direction points. Detail
     * that inverts with viewing angle inverts with distance.
     *
     * The shader now models the zinc-carbonate patina as the dielectric film it
     * is, which moves the rib into the diffuse channel. Two consequences here:
     *
     * `normalScale` comes down from 1.5, because the shape no longer has to be
     * shouted through the specular — and an aggressive normal on an 85 mm pitch
     * is exactly what was aliasing into moire at range, since mipmapping averages
     * normals toward flat but does nothing to the roughness that would have to
     * widen with them.
     *
     * `wear` comes down hard from 0.34. Bright bare metal painted along every
     * high-curvature rib shoulder is a 85 mm-pitch grid of thin white lines: the
     * single worst thing you can hand a mip chain, and on a galvanised sheet it
     * is not even true — the shoulders carry oxide, not polish.
     */
    normalScale: 1.1,
    envIntensity: 0.85,
    dirt: 0.70,
    dirtColor: srgb(0x1e1a15),
    wear: 0.10,
    wearColor: srgb(0x8d9094),
    wearMetal: 0.3,
    cavityAO: 0.62,
    seed: 37,
  },

  asphalt: {
    materialId: 'concrete',
    metal: 0,
    heightScale: 1.9,
    normalScale: 1.0,
    dirt: 0.40,
    dirtColor: srgb(0x1a1917),
    wear: 0.12,
    wearColor: srgb(0x6a6a6c),
    wearMetal: 0,
    cavityAO: 0.5,
    seed: 41,
  },

  dirt: {
    materialId: 'dirt',
    metal: 0,
    heightScale: 1.6,
    normalScale: 1.1,
    dirt: 0.45,
    dirtColor: srgb(0x2c2216),
    wear: 0.25,
    wearColor: srgb(0xb2a184),
    wearMetal: 0,
    cavityAO: 0.55,
    seed: 53,
  },

  brick: {
    materialId: 'concrete',
    metal: 0,
    heightScale: 2.6,
    normalScale: 1.25,
    dirt: 0.55,
    dirtColor: srgb(0x2f261c),
    wear: 0.30,
    wearColor: srgb(0xbdae9c),
    wearMetal: 0,
    cavityAO: 0.52,
    seed: 67,
  },

  wood: {
    materialId: 'wood',
    metal: 1, // nail heads are the only metal; the map keeps the planks at 0
    heightScale: 1.8,
    normalScale: 1.05,
    dirt: 0.50,
    dirtColor: srgb(0x241a11),
    wear: 0.28,
    wearColor: srgb(0xc9ab7d),
    wearMetal: 0,
    cavityAO: 0.5,
    seed: 71,
  },

  gunmetal: {
    materialId: 'metal',
    metal: 1,
    /**
     * The receiver had no three-dimensional read because its height field was
     * flat — measured mean normal slope 0.067, about 4 degrees — and the only
     * relief in it was the bead-blast cell pattern. Fed isotropic dots, the
     * curvature system in pass 2 answers with isotropic dots, so `wear` scattered
     * bright steel flecks evenly over faces and edges alike.
     *
     * The shader now carries milled flats meeting at crisp steps, which is a
     * *linear* curvature signal, so the same wear budget draws along edges instead
     * of speckling. `heightScale` and `normalScale` come up to let that structure
     * register; `cavityGain` comes up so the steps read as steps.
     *
     * Measured: wear contributes ~10% to mean albedo (0.060 -> 0.066 linear), so
     * the coverage was never the problem and is deliberately left alone.
     */
    heightScale: 1.7,
    normalScale: 0.85,
    sizeScale: 0.5,
    /** Chamfer facets have to see a *different* environment from the faces. */
    envIntensity: 1.7,
    dirt: 0.35,
    dirtColor: srgb(0x14141a),
    wear: 0.30,
    wearColor: srgb(0xc9ccd2),
    wearMetal: 1,
    cavityGain: 2.7,
    cavityAO: 0.38,
    macroTint: 0.07,
    macroRough: 0.06,
    seed: 83,
  },

  polymer: {
    materialId: 'wood',
    metal: 0,
    heightScale: 1.0,
    normalScale: 0.8,
    sizeScale: 0.5,
    dirt: 0.30,
    dirtColor: srgb(0x141414),
    /**
     * Measured: wear moves the mean albedo by 0.9% (0.1027 -> 0.1036 linear), so
     * it was never what made the stock bright — but its colour was neutral grey,
     * which pulled the surface back toward the very neutrality that renders as
     * salmon under a warm key. It is now an earth tone in the same family as the
     * albedo, and lighter rather than greyer, because abraded nylon goes chalky.
     */
    wear: 0.22,
    wearColor: srgb(0x8a8171),
    wearMetal: 0,
    cavityAO: 0.4,
    macroTint: 0.08,
    macroRough: 0.07,
    seed: 97,
  },

  glass: {
    materialId: 'glass',
    metal: 0,
    heightScale: 0.5,
    normalScale: 0.5,
    sizeScale: 0.25,
    envIntensity: 1.6,
    dirt: 0.30,
    dirtColor: srgb(0xb9c0c6), // dust on glass is *light*
    wear: 0.10,
    wearColor: srgb(0xdfe6ec),
    wearMetal: 0,
    cavityAO: 0.12,
    macroTint: 0.05,
    macroRough: 0.03,
    seed: 101,
  },

  fabric: {
    materialId: 'dirt',
    metal: 0,
    heightScale: 2.1,
    normalScale: 1.15,
    dirt: 0.55,
    dirtColor: srgb(0x2b2216),
    wear: 0.25,
    wearColor: srgb(0xcbbb9a),
    wearMetal: 0,
    cavityAO: 0.6,
    seed: 113,
  },

  tile: {
    materialId: 'concrete',
    metal: 0,
    heightScale: 2.5,
    normalScale: 1.2,
    /**
     * The ceramic floor was the brightest thing on screen at ~0.92 in interior
     * shade. Measured cause: 0.28 linear mean albedo *and* 72% of its texels below
     * roughness 0.31, so a light surface was also mirroring most of the sky.
     * The shader fixes both. Here, `wearColor` was 0xdcd6cb — 0.71 linear, three
     * times the surface it sits on — which put a bright rim on every tile edge and
     * grout line. Wear only moves the mean by 2%, but on a floor those rims are
     * exactly where the eye goes.
     */
    dirt: 0.48,
    dirtColor: srgb(0x3a332a),
    wear: 0.16,
    wearColor: srgb(0x9c968b),
    wearMetal: 0,
    cavityAO: 0.5,
    seed: 127,
  },
};
